import type { EventMember, EventTable, SfdcUser } from '../../event-manager-client';

export function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function isClayHost(member: EventMember, sfdcUsers: SfdcUser[]): boolean {
  // Clay employee if: email domain is clay.com, account is Clay, or they have an SFDC user record
  if (member.contact_email) {
    const email = member.contact_email.toLowerCase();
    if (email.endsWith('@clay.com')) return true;
    if (sfdcUsers.some(u => u.email.toLowerCase() === email)) return true;
  }
  if (member.account_name?.toLowerCase() === 'clay') return true;
  return false;
}

export function getAccountBadge(accountType: string | null): { label: string; variant: string } | null {
  switch (accountType) {
    case 'Enterprise': return { label: 'Enterprise', variant: 'matcha' };
    case 'Prospect': return { label: 'Prospect', variant: 'tangerine' };
    case 'Customer': return { label: 'Customer', variant: 'blueberry' };
    case null:
    default: return null;
  }
}

export function getShortName(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

export function computeSeatPositions(capacity: number): Array<{ xPct: number; yPct: number }> {
  const dim = getOvalDimensions(capacity);
  const positions: Array<{ xPct: number; yPct: number }> = [];
  for (let i = 0; i < capacity; i++) {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / capacity;
    positions.push({
      xPct: 50 + (dim.radiusX / (dim.radiusX + 36)) * 50 * Math.cos(angle),
      yPct: 50 + (dim.radiusY / (dim.radiusY + 36)) * 50 * Math.sin(angle),
    });
  }
  return positions;
}

export function getOvalDimensions(capacity: number): {
  containerW: number; containerH: number;
  innerRx: number; innerRy: number;
  radiusX: number; radiusY: number;
  dotSize: number;
} {
  const dotSize = capacity <= 6 ? 32 : capacity <= 8 ? 28 : 26;
  const radiusX = Math.max(60, 40 + capacity * 12);
  const radiusY = Math.max(45, 35 + capacity * 3);
  const pad = dotSize / 2 + 18;
  const containerW = (radiusX + pad) * 2;
  const containerH = (radiusY + pad) * 2;
  const innerRx = radiusX * 0.55;
  const innerRy = radiusY * 0.55;
  return { containerW, containerH, innerRx, innerRy, radiusX, radiusY, dotSize };
}

const TABLE_NAME_PATTERN = /^Table (\d+)$/;

// Computes the next default table name from the MAX numeric suffix among
// existing "Table N" names, plus 1 — not from tables.length. Length-based
// numbering collides after deletions (delete #2 of 3, add -> "Table 3" which
// already exists) and never advances on a failed create (retry proposes the
// exact same name, guaranteeing another identical 409). Names that don't
// match the "Table N" pattern (custom-named tables) are ignored, not thrown.
export function nextTableName(tables: EventTable[]): string {
  let maxSuffix = 0;
  for (const t of tables) {
    const match = TABLE_NAME_PATTERN.exec(t.table_name);
    if (match) {
      const n = Number(match[1]);
      if (n > maxSuffix) maxSuffix = n;
    }
  }
  return `Table ${maxSuffix + 1}`;
}
